import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";

import { Hamburger } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import useToast from "../hooks/useToast";
import {
  useApiQuery,
  useApiMutation,
  useInvalidateQueries,
} from "../hooks/useApiQuery";
import {
  getAllEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  getMyEvents,
  assignUsersToEvent,
  assignEventsToAdmin,
  removeAdminFromEvent,
} from "../services/api";
import api from "../services/api";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CalendarIcon,
  MapPinIcon,
  XMarkIcon,
  EllipsisHorizontalIcon,
  WalletIcon,
  TicketIcon,
  CakeIcon,
  QrCodeIcon,
  UsersIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { QRCodeCanvas } from "qrcode.react";

const EventsSuperAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const socket = useSocket();
  const { showSuccess, showError } = useToast();
  const invalidate = useInvalidateQueries();
  // const PUBLIC_BASE_URL = "http://192.168.1.44:3000";
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";

  // ✅ Use ref to prevent duplicate socket listeners
  const socketListenersRegistered = useRef(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRegisterQRModal, setShowRegisterQRModal] = useState(false);
  const [qrEvent, setQrEvent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    location: "",
    description: "",
    singleEntryOnly: false,
    eventType: "nfc",
  });

  // ✅ React Query: Fetch events
  const {
    data: eventsData = [],
    isLoading: eventsLoading,
    refetch: refetchEvents,
  } = useApiQuery(
    ["events"],
    () => {
      const fetchFn = isSuperAdmin ? getAllEvents : getMyEvents;
      return fetchFn().then((res) => {
        if (Array.isArray(res.data?.data)) return res.data.data;
        if (Array.isArray(res.data)) return res.data;
        return [];
      });
    },
    {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      // refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: () => showError("Failed to load events"),
    },
  );

  // ✅ React Query: Fetch all users
  const { data: allUsers = [], isLoading: usersLoading } = useApiQuery(
    ["users"],
    () =>
      api
        .get("/auth/users")

        .then((res) => res.data)
        .catch(() => []),

    {
      staleTime: 5 * 60 * 1000,
      // refetchOnMount: false,
      refetchOnWindowFocus: false,
      onError: () => console.error("Failed to fetch users"),
    },
  );

  // ✅ Memoized: Filter admins
  const admins = useMemo(() => {
    let eligible = allUsers.filter((u) =>
      ["super_admin", "event_admin", "event_editor"].includes(u.role),
    );
    eligible = eligible.filter((u) => u._id !== user?._id);
    return eligible;
  }, [allUsers, user]);

  // ✅ Memoized: Regular users
  const users = useMemo(() => {
    return allUsers.filter(
      (u) => u.role === "user" || u.role === "event_manager",
    );
  }, [allUsers]);

  const isLoading = eventsLoading || usersLoading;

  // ✅ React Query: Create event mutation
  const createMutation = useApiMutation(
    (data) => createEvent(data).then((res) => res.data),
    {
      onSuccess: () => {
        setShowCreateModal(false);
        resetForm();
        refetchEvents();
        showSuccess("Event created successfully");
      },
      onError: (error) => {
        console.error(error);
        alert(error.response?.data?.message || "Failed to create event");
      },
    },
  );

  // ✅ React Query: Update event mutation
  const updateMutation = useApiMutation(
    ({ id, data }) => updateEvent(id, data).then((res) => res.data),
    {
      onSuccess: () => {
        setShowEditModal(false);
        setSelectedEvent(null);
        resetForm();
        refetchEvents();
        showSuccess("Event updated successfully");
      },
      onError: (error) => {
        console.error("Error updating event:", error);
        showError(error.response?.data?.message || "Failed to update event");
      },
    },
  );

  // ✅ React Query: Delete event mutation
  const deleteMutation = useApiMutation(
    (id) => deleteEvent(id).then((res) => res.data),
    {
      onSuccess: () => {
        refetchEvents();
        showSuccess("Event deleted successfully");
      },
      onError: (error) => {
        console.error("Error deleting event:", error);
        showError(error.response?.data?.message || "Failed to delete event");
      },
    },
  );

  // ✅ React Query: Assign users to event mutation
  const assignUsersMutation = useApiMutation(
    ({ eventId, userIds }) =>
      assignUsersToEvent(eventId, userIds).then((res) => res.data),
    {
      onSuccess: () => {
        setShowAssignUsersModal(false);
        setSelectedUserIds([]);
        refetchEvents();
        showSuccess("Users assigned successfully");
      },
      onError: (error) => {
        console.error("Error assigning users:", error);
        showError(error.response?.data?.message || "Assignment failed");
      },
    },
  );

  // ✅ React Query: Assign event to admin mutation
  const assignAdminMutation = useApiMutation(
    ({ adminId, eventIds }) =>
      assignEventsToAdmin(adminId, eventIds).then((res) => res.data),
    {
      onSuccess: () => {
        setShowAssignAdminModal(false);
        setSelectedAdminId("");
        refetchEvents();
        showSuccess("Admin assigned successfully");
      },
      onError: (error) => {
        console.error("Error assigning event to admin:", error);
        showError(error.response?.data?.message || "Assignment failed");
      },
    },
  );

  // ✅ React Query: Remove admin from event mutation
  const removeAdminMutation = useApiMutation(
    ({ eventId, adminId }) =>
      removeAdminFromEvent(eventId, adminId).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccess("Admin removed from event");
        refetchEvents();
      },
      onError: (error) => {
        console.error("Error removing admin:", error);
        showError(error.response?.data?.message || "Failed to remove admin");
      },
    },
  );

  // ✅ Socket listeners
  // ✅ Socket listeners
  // ✅ Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleRefresh = () => {
      console.log("Socket event received, refetching events...");
      refetchEvents();
    };

    // Global user and event updates
    socket.on("userCreated", handleRefresh);
    socket.on("userUpdated", handleRefresh);
    socket.on("userDeleted", handleRefresh);
    socket.on("eventAssignmentChanged", handleRefresh);
    socket.on("eventCreated", handleRefresh);
    socket.on("eventDeleted", handleRefresh);
    socket.on("eventUpdated", handleRefresh);

    // ✅ Event-specific attendee updates
    socket.on("eventUserCreated", handleRefresh);
    socket.on("eventUserUpdated", handleRefresh);
    socket.on("eventUserDeleted", handleRefresh);
    socket.on("eventUsersCopied", handleRefresh);

    return () => {
      socket.off("userCreated", handleRefresh);
      socket.off("userUpdated", handleRefresh);
      socket.off("userDeleted", handleRefresh);
      socket.off("eventAssignmentChanged", handleRefresh);
      socket.off("eventCreated", handleRefresh);
      socket.off("eventDeleted", handleRefresh);
      socket.off("eventUpdated", handleRefresh);

      socket.off("eventUserCreated", handleRefresh);
      socket.off("eventUserUpdated", handleRefresh);
      socket.off("eventUserDeleted", handleRefresh);
      socket.off("eventUsersCopied", handleRefresh);
    };
  }, [socket, refetchEvents]);

  // ✅ Handlers
  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      startDate: "",
      endDate: "",
      location: "",
      description: "",
      singleEntryOnly: false,
      eventType: "nfc",
    });
  }, []);

  const handleCreateEvent = useCallback(
    (e) => {
      e.preventDefault();
      createMutation.mutate({
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
      });
    },
    [formData, createMutation],
  );

  const handleUpdateEvent = useCallback(
    (e) => {
      e.preventDefault();
      if (!selectedEvent) return;
      updateMutation.mutate({
        id: selectedEvent._id,
        data: {
          ...formData,
          startDate: new Date(formData.startDate),
          endDate: new Date(formData.endDate),
        },
      });
    },
    [selectedEvent, formData, updateMutation],
  );

  const handleDeleteEvent = useCallback(
    (eventId) => {
      if (
        window.confirm(
          "Are you sure you want to delete this event? This action cannot be undone.",
        )
      ) {
        deleteMutation.mutate(eventId);
      }
    },
    [deleteMutation],
  );

  const handleAssignUsers = useCallback(() => {
    if (!selectedEvent) return;
    assignUsersMutation.mutate({
      eventId: selectedEvent._id,
      userIds: selectedUserIds,
    });
  }, [selectedEvent, selectedUserIds, assignUsersMutation]);

  const handleAssignAdmin = useCallback(
    (e) => {
      if (e) e.preventDefault();
      if (!selectedEvent || !selectedAdminId) return;
      assignAdminMutation.mutate({
        adminId: selectedAdminId,
        eventIds: [selectedEvent._id],
      });
    },
    [selectedEvent, selectedAdminId, assignAdminMutation],
  );

  const handleRemoveAdmin = useCallback(
    (eventId, adminId) => {
      if (window.confirm("Remove this admin from the event?")) {
        removeAdminMutation.mutate({ eventId, adminId });
      }
    },
    [removeAdminMutation],
  );

  const openEditModal = useCallback((event) => {
    setSelectedEvent(event);
    setFormData({
      name: event.name,
      startDate: event.startDate ? event.startDate.split("T")[0] : "",
      endDate: event.endDate ? event.endDate.split("T")[0] : "",
      location: event.location,
      description: event.description || "",
      singleEntryOnly: event.singleEntryOnly || false,
      eventType: event.eventType || "nfc",
    });
    setShowEditModal(true);
  }, []);

  const toggleDropdown = useCallback((eventId) => {
    setOpenDropdownId((prev) => (prev === eventId ? null : eventId));
  }, []);

  // ✅ Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest(".dropdown-container")) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  // ✅ Date Formatting Helper (e.g., 10 Oct 2026 – 12 Oct 2026)
  const formatDateRange = (start, end) => {
    if (!start || !end) return "";
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startStr = startDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const endStr = endDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${startStr} – ${endStr}`;
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Events</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create, edit, and manage all events
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-5 py-2.5 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          Create Event
        </button>
      </div>

      {/* Events Grid */}
      {eventsData.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
          <CalendarIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No events created yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center gap-2 text-[#1672b9] hover:text-[#0f5a9e] hover:underline transition-all"
          >
            <PlusIcon className="w-4 h-4" />
            Create your first event
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {eventsData.map((event) => (
            <div
              key={event._id}
              className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col relative overflow-hidden"
            >
              {/* Top-right dropdown container */}
              <div className="absolute top-3 right-3 z-10 dropdown-container">
                <button
                  onClick={() => toggleDropdown(event._id)}
                  className={`p-1.5 rounded-md transition-colors ${
                    openDropdownId === event._id
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  }`}
                  aria-label="More actions"
                >
                  <EllipsisHorizontalIcon className="w-4 h-4" />
                </button>

                {openDropdownId === event._id && (
                  <div className="absolute right-0 top-8 w-44 bg-white rounded-lg border border-gray-200 shadow-lg z-[9999] py-1">
                    {isSuperAdmin && (
                      <button
                        onClick={() => {
                          setSelectedEvent(event);
                          setShowAssignAdminModal(true);
                          setOpenDropdownId(null);
                        }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      >
                        <UserPlusIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                        Assign to User
                      </button>
                    )}
                    {/* <button
                      onClick={() => {
                        navigate(`/events/${event._id}/sessions`);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <DocumentTextIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                      Session Logs
                    </button> */}
                    <button
                      onClick={() => {
                        navigate(`/events/${event._id}/wallet`);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <WalletIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                      Wallet
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        openEditModal(event);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <PencilIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                      Edit Event
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteEvent(event._id);
                        setOpenDropdownId(null);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors text-left"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      Delete Event
                    </button>
                  </div>
                )}
              </div>

              {/* Card Content */}
              <div className="p-3 flex-1 flex flex-col">
                {/* Header */}
                <div className="flex items-center gap-1.5 mb-1.5 pr-5">
                  <h3 className="text-[13px] font-bold text-[#1672b9] tracking-tight truncate">
                    {event.name}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-0.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full shrink-0 bg-blue-50 text-[#1672b9]`}
                  >
                    {event.eventType === "qr" ? (
                      <QrCodeIcon className="w-2.5 h-2.5" />
                    ) : (
                      <DevicePhoneMobileIcon className="w-2.5 h-2.5" />
                    )}
                    {event.eventType === "qr" ? "QR" : "NFC"}
                  </span>
                </div>

                {/* Details */}
                <div className="space-y-0.5 text-[11px] text-gray-500 mb-2">
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-3 h-3" />
                    <span>
                      {formatDateRange(event.startDate, event.endDate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPinIcon className="w-3 h-3" />
                    <span>{event.location}</span>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  {/* Session Logs Box (formerly Scans Box) */}
                  <button
                    onClick={() => navigate(`/events/${event._id}/sessions`)}
                    className="bg-blue-50 rounded-lg p-1.5 flex items-center gap-1.5 hover:bg-blue-100 transition-colors text-left"
                  >
                    <div className="p-1 bg-blue-100 rounded-md">
                      <DocumentTextIcon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        {event.totalScans || 0}
                      </div>
                      <div className="text-[8px] text-gray-500 font-medium">
                        Session Logs
                      </div>
                    </div>
                  </button>
                  {/* Users Box - Acts as View Users Button */}
                  <button
                    onClick={() => navigate(`/events/${event._id}/users`)}
                    className="bg-blue-50 rounded-lg p-1.5 flex items-center gap-1.5 hover:bg-blue-100 transition-colors text-left"
                  >
                    <div className="p-1 bg-blue-100 rounded-md">
                      <UsersIcon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900">
                        {event.users?.length || 0}
                      </div>
                      <div className="text-[8px] text-gray-500 font-medium">
                        Users
                      </div>
                    </div>
                  </button>
                </div>

                {/* Action Grid */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <button
                    onClick={() =>
                      navigate(`/events/${event._id}/dashboard/overview`)
                    }
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <ChartBarIcon className="w-3.5 h-3.5 text-[#1672b9] group-hover:text-white" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Dashboard
                      </span>
                    </div>
                  </button>

                  <button
                    onClick={() => navigate(`/events/${event._id}/meals`)}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <Hamburger
                        size={14}
                        className="text-[#1672b9] group-hover:text-white"
                      />

                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Meal Coupons
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setQrEvent(event);
                      setShowRegisterQRModal(true);
                    }}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <QrCodeIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Registration QR
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() =>
                      navigate(`/events/${event._id}/ticket-types`)
                    }
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <TicketIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Ticket Types
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => navigate(`/events/${event._id}/devices`)}
                    className="group flex items-center justify-between p-1.5 rounded-lg border border-gray-100 bg-white hover:bg-[#1471B9] transition-colors text-left"
                  >
                    <div className="flex items-center gap-1.5">
                      <DevicePhoneMobileIcon className="w-3.5 h-3.5 text-[#1672b9]" />
                      <span className="text-[9px] font-semibold text-gray-700 group-hover:text-white">
                        Devices
                      </span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Assigned Admins Section */}
              {isSuperAdmin && (
                <div className="px-3 pb-3 pt-2 border-t border-gray-100 mt-auto">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#1672b9]"></div>
                    <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">
                      Assigned Admins
                    </span>
                  </div>
                  {event.assignedAdmins?.length > 0 ? (
                    <ul className="space-y-0.5">
                      {event.assignedAdmins
                        .filter((a) => {
                          const adminRoles = [
                            "admin",
                            "event_admin",
                            "event_editor",
                            "super_admin",
                          ];
                          const isAdminRole = adminRoles.includes(a.role);
                          const isSuperAdminBlocked =
                            user?.role === "admin" && a.role === "super_admin";
                          return isAdminRole && !isSuperAdminBlocked;
                        })
                        .map((admin) => (
                          <li
                            key={admin._id}
                            className="flex items-center justify-between text-[9px] py-0.5"
                          >
                            <span className="text-gray-700 font-medium truncate max-w-[120px]">
                              {admin.name}
                            </span>
                            <button
                              onClick={() =>
                                handleRemoveAdmin(event._id, admin._id)
                              }
                              disabled={removeAdminMutation.isPending}
                              className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="text-[9px] text-gray-400">
                      No admin assigned
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <Modal
          title="Create New Event"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateEvent}
          submitLabel="Create Event"
        >
          <FormField
            label="Event Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <FormField
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) =>
              setFormData({ ...formData, startDate: e.target.value })
            }
            required
          />
          <FormField
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) =>
              setFormData({ ...formData, endDate: e.target.value })
            }
            required
          />
          <FormField
            label="Location"
            type="text"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            required
          />
          <FormField
            label="Description"
            type="textarea"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={3}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, eventType: "nfc" })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.eventType === "nfc"
                    ? "border-[#1672b9] bg-[#1672b9]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="font-semibold text-sm text-gray-900">NFC Event</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  ID assigned at the entrance desk
                </p>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, eventType: "qr" })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.eventType === "qr"
                    ? "border-[#1672b9] bg-[#1672b9]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="font-semibold text-sm text-gray-900">
                  QR Code Event
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Attendees get a personal QR after signup
                </p>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  singleEntryOnly: !formData.singleEntryOnly,
                })
              }
              className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                formData.singleEntryOnly ? "bg-[#1672B9]" : "bg-gray-300"
              } relative`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                  formData.singleEntryOnly ? "translate-x-6" : "translate-x-0"
                }`}
              />
            </button>
            <label className="text-sm font-medium text-gray-700">
              Allow only one entry per attendee
            </label>
          </div>
        </Modal>
      )}

      {showEditModal && selectedEvent && (
        <Modal
          title="Edit Event"
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateEvent}
          submitLabel="Update Event"
        >
          <FormField
            label="Event Name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <FormField
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) =>
              setFormData({ ...formData, startDate: e.target.value })
            }
            required
          />
          <FormField
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) =>
              setFormData({ ...formData, endDate: e.target.value })
            }
            required
          />
          <FormField
            label="Location"
            type="text"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            required
          />
          <FormField
            label="Description"
            type="textarea"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={3}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, eventType: "nfc" })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.eventType === "nfc"
                    ? "border-[#1672b9] bg-[#1672b9]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="font-semibold text-sm text-gray-900">NFC Event</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  ID assigned at the entrance desk
                </p>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, eventType: "qr" })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  formData.eventType === "qr"
                    ? "border-[#1672b9] bg-[#1672b9]/5"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <p className="font-semibold text-sm text-gray-900">
                  QR Code Event
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Attendees get a personal QR after signup
                </p>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                setFormData({
                  ...formData,
                  singleEntryOnly: !formData.singleEntryOnly,
                })
              }
              className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                formData.singleEntryOnly ? "bg-[#1672B9]" : "bg-gray-300"
              } relative`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${
                  formData.singleEntryOnly ? "translate-x-6" : "translate-x-0"
                }`}
              />
            </button>
            <label className="text-sm font-medium text-gray-700">
              Allow only one entry per attendee
            </label>
          </div>
        </Modal>
      )}

      {showAssignUsersModal && selectedEvent && (
        <Modal
          title={`Assign Users to ${selectedEvent.name}`}
          onClose={() => setShowAssignUsersModal(false)}
          onSubmit={handleAssignUsers}
          submitLabel="Assign Users"
        >
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Users
            </label>
            <select
              multiple
              value={selectedUserIds}
              onChange={(e) =>
                setSelectedUserIds(
                  Array.from(e.target.selectedOptions, (o) => o.value),
                )
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] h-32"
              disabled={assignUsersMutation.isPending}
            >
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Hold Ctrl/Cmd to select multiple
            </p>
          </div>
        </Modal>
      )}

      {showRegisterQRModal && qrEvent && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Registration QR
              </h3>
              <button
                onClick={() => {
                  setShowRegisterQRModal(false);
                  setQrEvent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mb-4">
              <p className="text-sm text-gray-500 mb-1">
                Show this QR at the event entrance
              </p>
              <p className="text-xs text-gray-400">
                Attendees scan → fill form → get their personal QR
              </p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-center mb-4">
              <QRCodeCanvas
                value={`${window.location.origin}/event/${qrEvent._id}/register`}
                size={240}
              />
            </div>
            {/* <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-center mb-4">
              <QRCodeCanvas
                value={`${PUBLIC_BASE_URL}/event/${qrEvent._id}/register`}
                size={240}
              />
            </div> */}

            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Or share this link:</p>
              <p className="text-xs font-mono text-gray-700 break-all">
                {`${window.location.origin}/event/${qrEvent._id}/register`}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/event/${qrEvent._id}/register`,
                  );
                  showSuccess("Link copied");
                }}
                className="flex-1 bg-white border border-gray-200 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Copy Link
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 bg-[#1672b9] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#0f5a9e]"
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignAdminModal && selectedEvent && (
        <Modal
          title={`Assign "${selectedEvent.name}" to User`}
          onClose={() => setShowAssignAdminModal(false)}
          onSubmit={handleAssignAdmin}
          submitLabel="Assign to User"
        >
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select User (Admin, Event Admin, or Event Editor)
            </label>
            <select
              value={selectedAdminId}
              onChange={(e) => setSelectedAdminId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
              required
              disabled={assignAdminMutation.isPending}
            >
              <option value="">-- Select a user --</option>
              {admins.map((admin) => (
                <option key={admin._id} value={admin._id}>
                  {admin.name} – {admin.role.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Reusable components
const Modal = ({ title, onClose, onSubmit, submitLabel, children }) => (
  <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
    <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-[#1672b9] transition-colors"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <form onSubmit={onSubmit} className="p-5 space-y-4">
        {children}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition-all shadow-sm"
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  </div>
);

const FormField = ({ label, type, value, onChange, required, rows }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    {type === "textarea" ? (
      <textarea
        value={value}
        onChange={onChange}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
        rows={rows || 3}
        required={required}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
        required={required}
      />
    )}
  </div>
);

export default EventsSuperAdmin;
